// config/sentry.ts - Sentry error tracking configuration
import * as Sentry from '@sentry/react';
import env from './env';
import logger from '../utils/logger';

// Helper to get environment variables safely
const getEnvVar = (viteKey: string, craKey: string): string | undefined => {
  // Try Vite env first (for Vite build)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[viteKey];
  }
  // Fall back to process.env (for CRA build)
  if (typeof process !== 'undefined' && process.env) {
    return (process.env as any)[craKey];
  }
  return undefined;
};

// Check if we're in production or staging
const isProduction = env.NODE_ENV === 'production' || env.ENV === 'production';
const isDevelopment = env.NODE_ENV === 'development' || env.ENV === 'development';

// Sentry configuration
export const initSentry = () => {
  // Only initialize in production or if explicitly enabled in development
  if (!isProduction && !getEnvVar('VITE_ENABLE_SENTRY', 'REACT_APP_ENABLE_SENTRY')) {
    logger.debug('[Sentry] Skipping initialization in development');
    return;
  }

  const dsn = getEnvVar('VITE_SENTRY_DSN', 'REACT_APP_SENTRY_DSN');
  
  if (!dsn) {
    console.warn('[Sentry] No DSN provided, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.replayIntegration({
        // Mask all text and inputs by default for privacy
        maskAllText: true,
        maskAllInputs: true,
        // Only capture replays on errors in production
        sessionSampleRate: isDevelopment ? 1.0 : 0.1,
        errorSampleRate: 1.0,
      } as any),
    ],
    
    // Performance Monitoring
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Environment
    environment: env.ENV,
    
    // Release tracking
    release: getEnvVar('VITE_APP_VERSION', 'REACT_APP_VERSION'),
    
    // User privacy
    beforeSend(event, hint) {
      // Filter out sensitive information
      if (event.request?.cookies) {
        delete event.request.cookies;
      }
      
      // Don't send events in development unless explicitly enabled
      if (isDevelopment && !getEnvVar('VITE_ENABLE_SENTRY', 'REACT_APP_ENABLE_SENTRY')) {
        return null;
      }
      
      // Filter out specific errors
      const error = hint.originalException;
      
      // Ignore network errors that are expected (offline, etc)
      if (error && error instanceof Error) {
        if (error.message?.includes('Network request failed')) {
          return null;
        }
        if (error.message?.includes('Load failed')) {
          return null;
        }
        // Ignore ResizeObserver errors (common and harmless)
        if (error.message?.includes('ResizeObserver loop limit exceeded')) {
          return null;
        }
      }
      
      return event;
    },
    
    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'chrome-extension',
      'moz-extension',
      // Facebook related errors
      'fb_xd_fragment',
      // Network errors (handled by the app)
      'NetworkError',
      'Request aborted',
      'Request failed',
      // Common harmless errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Service Worker errors (handled separately)
      'Failed to update a ServiceWorker',
    ],
  });

  // Set user context when available
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const userData = JSON.parse(user);
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
      });
    } catch (error) {
      logger.error('[Sentry] Failed to set user context:', error);
    }
  }

  logger.info('[Sentry] Initialized successfully');
};

// Export Sentry for manual error capturing
export { Sentry };

// Custom error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Helper to capture exceptions manually
export const captureException = (error: Error | unknown, context?: Record<string, any>) => {
  if (!isProduction && !getEnvVar('VITE_ENABLE_SENTRY', 'REACT_APP_ENABLE_SENTRY')) {
    console.error('Error captured (Sentry disabled):', error, context);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper to capture messages
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  if (!isProduction && !getEnvVar('VITE_ENABLE_SENTRY', 'REACT_APP_ENABLE_SENTRY')) {
    logger.debug(`Message captured (Sentry disabled) [${level}]:`, message);
    return;
  }
  
  Sentry.captureMessage(message, level);
};

// Helper to add breadcrumbs
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  if (!isProduction && !getEnvVar('VITE_ENABLE_SENTRY', 'REACT_APP_ENABLE_SENTRY')) {
    return;
  }
  
  Sentry.addBreadcrumb(breadcrumb);
};

// Performance monitoring helpers
export const startTransaction = (name: string, op: string) => {
  return Sentry.startSpan({ name, op }, () => {});
};

// Profile specific operations
export const profileOperation = async <T>(
  operationName: string,
  operation: () => Promise<T>
): Promise<T> => {
  return Sentry.startSpan({ name: operationName, op: 'operation' }, async () => {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      captureException(error, { operation: operationName });
      throw error;
    }
  });
};